import { SupportedLocales } from '@/models/localization.model';

export enum SettingsEndpoints {
  SETTINGS = '/settings',
}

export enum FieldTypes {
  ORDER_EMAILS = 'order_emails',
  ADMIN_EMAILS = 'admin_emails',
  PRICE_CHANGE_ADMIN_EMAILS = 'price_change_admin_emails',
  GOOGLE_ANALYTICS = 'google_analytics',
  CONTACT_EMAIL = 'contact_email',
  CONTACT_PHONE = 'contact_phone',
  SOCIAL_LINKS = 'social_links',
  ADDRESS = 'address',
  WORKING_HOURS = 'working_hours',
  EXTERNAL_MAP_ID = 'external_map_id',
  LATITUDE = 'latitude',
  LONGITUDE = 'longitude',
  FOP_MERCHANT = 'fop_merchant',
  TOV_MERCHANT = 'tov_merchant',
  FOP_MERCHANT_DESCRIPTION = 'fop_merchant_description',
  TOV_MERCHANT_DESCRIPTION = 'tov_merchant_description',
  MIN_ORDER_TOTAL = 'min_order_total',
  FOP_CASH_REGISTER_ID = 'fop_cash_register_id',
  TOV_CASH_REGISTER_ID = 'tov_cash_register_id',
  FOP_CASHIER_NAME = 'fop_cashier_name',
  TOV_CASHIER_NAME = 'tov_cashier_name',
  FOP_FISCAL_DEPARTMENT = 'fop_fiscal_department',
  TOV_FISCAL_DEPARTMENT = 'tov_fiscal_department',
}

export enum SocialMedia {
  TELEGRAM = 'telegram',
  VIBER = 'viber',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  TWITTER = 'twitter',
  THREADS = 'threads',
  TIK_TOK = 'tiktok',
  YOUTUBE = 'youtube',
}

export interface IAdminEmailsType {
  id: string;
  value: string[];
  type: FieldTypes.ADMIN_EMAILS;
}

export interface IOrderEmailsType {
  id: string;
  value: string[];
  type: FieldTypes.ORDER_EMAILS;
}

export interface IPriceChangeAdminEmailsType {
  id: string;
  value: string[];
  type: FieldTypes.PRICE_CHANGE_ADMIN_EMAILS;
}

export interface IGoogleAnalyticsType {
  id: string;
  value: string;
  type: FieldTypes.GOOGLE_ANALYTICS;
}

export interface ILatLngType {
  id: string;
  value: string;
  type: FieldTypes.LATITUDE | FieldTypes.LONGITUDE | FieldTypes.EXTERNAL_MAP_ID;
}

export interface IContactEmailType {
  id: string;
  value: string;
  type: FieldTypes.CONTACT_EMAIL;
}

export interface IContactPhoneType {
  id: string;
  value: string;
  type: FieldTypes.CONTACT_PHONE;
}

export interface ISocialLinksType {
  id: string;
  value: Record<SocialMedia, string>;
  type: FieldTypes.SOCIAL_LINKS;
}

export interface IAddressType {
  id: string;
  value: string;
  type: FieldTypes.ADDRESS;
  locale: Record<
    SupportedLocales,
    {
      name: string;
    }
  >;
}

export interface IFiscalSettingType {
  id: string;
  value: string;
  type:
    | FieldTypes.FOP_CASH_REGISTER_ID
    | FieldTypes.TOV_CASH_REGISTER_ID
    | FieldTypes.FOP_CASHIER_NAME
    | FieldTypes.TOV_CASHIER_NAME
    | FieldTypes.FOP_FISCAL_DEPARTMENT
    | FieldTypes.TOV_FISCAL_DEPARTMENT;
}

export interface IMerchantType {
  id: string;
  value: string;
  type:
    | FieldTypes.FOP_MERCHANT
    | FieldTypes.TOV_MERCHANT
    | FieldTypes.FOP_MERCHANT_DESCRIPTION
    | FieldTypes.TOV_MERCHANT_DESCRIPTION;
  locale: Record<
    SupportedLocales,
    {
      name: string;
    }
  >;
}

export interface IWorkingHoursType {
  id: string;
  type: FieldTypes.WORKING_HOURS;
  locale: Record<
    SupportedLocales,
    {
      name: string;
    }
  >;
}

export interface IMinOrderTotalType {
  id: string;
  value: string | number;
  type: FieldTypes.MIN_ORDER_TOTAL;
}

export type ISettings = [
  IAdminEmailsType,
  IOrderEmailsType,
  IPriceChangeAdminEmailsType,
  IGoogleAnalyticsType,
  IContactEmailType,
  IContactPhoneType,
  ISocialLinksType,
  IAddressType,
  IWorkingHoursType,
  IMerchantType,
  IMinOrderTotalType,
];

export type ISettingsMap = {
  [FieldTypes.ADDRESS]: IAddressType;
  [FieldTypes.CONTACT_EMAIL]: IContactEmailType;
  [FieldTypes.CONTACT_PHONE]: IContactPhoneType;
  [FieldTypes.GOOGLE_ANALYTICS]: IGoogleAnalyticsType;
  [FieldTypes.ORDER_EMAILS]: IOrderEmailsType;
  [FieldTypes.ADMIN_EMAILS]: IAdminEmailsType;
  [FieldTypes.PRICE_CHANGE_ADMIN_EMAILS]: IPriceChangeAdminEmailsType;
  [FieldTypes.SOCIAL_LINKS]: ISocialLinksType;
  [FieldTypes.WORKING_HOURS]: IWorkingHoursType;
  [FieldTypes.LATITUDE]: ILatLngType;
  [FieldTypes.LONGITUDE]: ILatLngType;
  [FieldTypes.EXTERNAL_MAP_ID]: ILatLngType;
  [FieldTypes.FOP_MERCHANT]: IMerchantType;
  [FieldTypes.TOV_MERCHANT]: IMerchantType;
  [FieldTypes.FOP_MERCHANT_DESCRIPTION]: IMerchantType;
  [FieldTypes.TOV_MERCHANT_DESCRIPTION]: IMerchantType;
  [FieldTypes.MIN_ORDER_TOTAL]: IMinOrderTotalType;
  [FieldTypes.FOP_CASH_REGISTER_ID]: IFiscalSettingType;
  [FieldTypes.TOV_CASH_REGISTER_ID]: IFiscalSettingType;
  [FieldTypes.FOP_CASHIER_NAME]: IFiscalSettingType;
  [FieldTypes.TOV_CASHIER_NAME]: IFiscalSettingType;
  [FieldTypes.FOP_FISCAL_DEPARTMENT]: IFiscalSettingType;
  [FieldTypes.TOV_FISCAL_DEPARTMENT]: IFiscalSettingType;
};

export type ISettingsTypes = ISettings[number];

// ----------------- DTOs ----------------

export interface IUpdateSettingsDTO {
  [FieldTypes.ADMIN_EMAILS]: string[];
  [FieldTypes.ORDER_EMAILS]: string[];
  [FieldTypes.PRICE_CHANGE_ADMIN_EMAILS]: string[];
  [FieldTypes.CONTACT_EMAIL]: string;
  [FieldTypes.CONTACT_PHONE]: string;
  [FieldTypes.GOOGLE_ANALYTICS]?: string;
  [FieldTypes.SOCIAL_LINKS]: Record<SocialMedia, string | undefined>;
  [FieldTypes.ADDRESS]: {
    locale: Record<SupportedLocales, { name: string }>;
  };
  [FieldTypes.WORKING_HOURS]: {
    locale: Record<SupportedLocales, { name: string }>;
  };
  external_map_id?: string;
  latitude?: string;
  longitude?: string;
  fop_merchant?: string;
  tov_merchant?: string;
  fop_merchant_description?: string;
  tov_merchant_description?: string;
  [FieldTypes.MIN_ORDER_TOTAL]: number;
  fop_cash_register_id?: string;
  tov_cash_register_id?: string;
  fop_cashier_name?: string;
  tov_cashier_name?: string;
  fop_fiscal_department?: string;
  tov_fiscal_department?: string;
}

// ------------ Responses --------------

export interface IGetSettingsResponse {
  data: ISettings;
}

export interface IUpdateSettingsResponse {
  data: ISettings;
}
